import type { Client } from '@/types';
import { formatDateForDisplay, getTimeUntilDay } from '@/shared/lib/calendarDate';
import { getPassMeta, getPassTone } from '@/shared/lib/client';
import { addS } from '@/shared/lib/addS';
import { toneColor } from '@/shared/ui';

export interface PassDescription {
  text: string;
  color?: string;
}

const formatExpiry = (months: number, days: number): string => {
  if (months === 0) return `${days} ${addS(days, 'day')}`;
  if (days === 0) return `${months} ${addS(months, 'month')}`;

  return `${months} ${addS(months, 'month')} ${days} ${addS(days, 'day')}`;
};

export const getPassDescription = (client: Client): PassDescription => {
  if (!client.pass) {
    return { text: 'No pass' };
  }

  const meta = getPassMeta(client.pass);
  const tone = getPassTone(meta);

  if (meta?.isExpired) {
    return { text: 'Expired', color: toneColor('destructive') };
  }

  if (meta?.isFuture) {
    return { text: `Starts ${formatDateForDisplay(client.pass.startsAt)}` };
  }

  if (meta?.isOutOfCredits) {
    return { text: 'No credits left', color: toneColor('destructive') };
  }

  if (!meta?.isActive) {
    return { text: 'Unknown' };
  }

  if (!client.pass.endsAt) {
    return { text: 'No expiry' };
  }

  const timeUntilExpiry = getTimeUntilDay(client.pass.endsAt);
  if (!timeUntilExpiry) {
    return { text: 'Expired', color: toneColor('destructive') };
  }

  const { months, days, calendarDayOffset } = timeUntilExpiry;

  if (calendarDayOffset === 0) {
    return { text: 'Expires today', color: toneColor('warning') };
  }
  if (calendarDayOffset === 1) {
    return { text: 'Expires tomorrow', color: toneColor('warning') };
  }

  const text = `Expires in ${formatExpiry(months, days)}`;
  if (tone === 'warning') {
    return { text, color: toneColor('warning') };
  }
  return { text };
};
